import { useCallback, useMemo } from 'react';
import { LANGUAGES, USER_TAGS_KEYS, ALLOWED_PERMISSIONS_FOR_CHANGE } from '../constants';
import { getLang } from '../store/selectors/lang.selector';
import { shallowEqual, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { camelCaseToWords, sortByAlphabet } from '../utils';
import { nanoid } from 'nanoid';
import { useRotationOptions } from './profile.context';
import { useProfileFields } from './profile-fields.hooks';
import { getLabelsList } from '../store/selectors/labels.selector';
import _ from 'lodash';
import { getWorkspaces } from '../store/selectors/workspace.selector';

// Flatten the grouped GET /profile-fields response into a single array of fields.
const flattenProfileFields = byGroup =>
  byGroup ? [...(byGroup.ORGANIZATIONAL || []), ...(byGroup.PROFESSIONAL || [])] : [];

// Index the flattened fields by their fieldKey.
const buildProfileFieldsByKey = byGroup =>
  flattenProfileFields(byGroup).reduce((acc, field) => {
    acc[field.fieldKey] = field;
    return acc;
  }, {});

// Map GET /profile-fields option objects to { value, label } select options.
const mapProfileFieldOptions = options =>
  (options || [])
    .map(({ code, label }) => ({ value: code, label: label || code }))
    .sort((a, b) => sortByAlphabet(a?.label, b?.label));

const useProfileTagsOption = options => {
  const locations = useSelector(getLabelsList, shallowEqual);

  const { stats = {}, showStats = true } = options;

  const createOptions = (values, labels, key, stats) => {
    let valuesToParse = values;

    if (!Array.isArray(values)) {
      valuesToParse = Object.values(values || {});
    }

    // If there are no stats for the key, return an empty array
    if (Object.keys(stats?.[key] || {}).length === 0) {
      return [];
    }

    return valuesToParse
      .filter(el => stats?.[key]?.[el])
      .map(el => {
        const keyStats = stats?.[key];
        const value = `${key}.${el}`;
        const label = `${labels?.[el] ?? el}${
          keyStats[el] && showStats ? ` (${keyStats[el]})` : ''
        }`;
        return { value, label, key: nanoid() };
      })
      .sort((a, b) => sortByAlphabet(a?.label, b?.label));
  };

  const useOptions = (values, labels, key, lang, stats) =>
    useMemo(
      () => createOptions(values, labels, key, stats),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [lang, stats],
    );

  const { data: profileFieldsByGroup } = useProfileFields();
  const fieldsByKey = useMemo(
    () => buildProfileFieldsByKey(profileFieldsByGroup),
    [profileFieldsByGroup],
  );

  // Per-org facet: values and labels both come from the org's own field options
  // (the source of truth). createOptions filters by `stats`, so a facet still
  // lists only options that at least one user has.
  const usePerOrgTagOptions = (fieldKey, tagKey) =>
    useMemo(
      () => {
        const fieldOptions = fieldsByKey[fieldKey]?.options || [];
        const labels = fieldOptions.reduce((acc, option) => {
          acc[option.code] = option.label;
          return acc;
        }, {});
        return createOptions(
          fieldOptions.map(option => option.code),
          labels,
          tagKey,
          stats,
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [fieldsByKey, stats],
    );

  const departmentOptions = usePerOrgTagOptions('department', USER_TAGS_KEYS.DEPARTMENT);

  const professionOptions = usePerOrgTagOptions('profession', USER_TAGS_KEYS.PROFESSION);

  const roleOptions = usePerOrgTagOptions('role', USER_TAGS_KEYS.ROLE);

  const levelOfTrainingOptions = usePerOrgTagOptions(
    'levelOfTraining',
    USER_TAGS_KEYS.LEVEL_OF_TRAINING,
  );

  const locationMap =
    locations?.reduce((obj, { code, name }) => {
      obj[code] = code;
      return obj;
    }, {}) || {};

  const locationLabels =
    locations?.reduce((obj, { code, name }) => {
      obj[code] = name;
      return obj;
    }, {}) || {};

  const locationOptions = useOptions(
    locationMap,
    locationLabels,
    USER_TAGS_KEYS.LOCATION,
    locationMap,
    stats,
  );

  const { getFilteredRotationOptions } = useRotationOptions();

  const permissionsLabels = ALLOWED_PERMISSIONS_FOR_CHANGE.reduce((obj, permission) => {
    obj[permission] = camelCaseToWords(permission);
    return obj;
  }, {});

  const permissionsOptions = useOptions(
    ALLOWED_PERMISSIONS_FOR_CHANGE,
    permissionsLabels,
    USER_TAGS_KEYS.PERMISSIONS,
    permissionsLabels,
    stats,
  );
  const specialtyOptions = usePerOrgTagOptions('specialty', USER_TAGS_KEYS.SPECIALTY);

  const divisionOptions = usePerOrgTagOptions('division', USER_TAGS_KEYS.DIVISION);

  const unitOptions = usePerOrgTagOptions('unit', USER_TAGS_KEYS.UNIT);

  return {
    departmentOptions,
    rotationOptions: getFilteredRotationOptions(stats, USER_TAGS_KEYS.ROTATION, showStats),
    professionOptions,
    roleOptions,
    levelOfTrainingOptions,
    locationOptions,
    permissionsOptions,
    specialtyOptions,
    divisionOptions,
    unitOptions,
  };
};

// Flat per-org profession / role / levelOfTraining option lists (no parent
// filtering) for multi-select surfaces like the schedule-URL editor, which
// target several professions/roles at once. Sourced from GET /profile-fields so
// the stored codes match user records.
const useProfileOptionsField = () => {
  const { data: profileFieldsByGroup } = useProfileFields();

  const fieldsByKey = useMemo(
    () => buildProfileFieldsByKey(profileFieldsByGroup),
    [profileFieldsByGroup],
  );

  const professionOptions = useMemo(
    () => mapProfileFieldOptions(fieldsByKey.profession?.options),
    [fieldsByKey],
  );

  const roleOptions = useMemo(
    () => mapProfileFieldOptions(fieldsByKey.role?.options),
    [fieldsByKey],
  );

  const levelOftrainingOptions = useMemo(
    () => mapProfileFieldOptions(fieldsByKey.levelOfTraining?.options),
    [fieldsByKey],
  );

  return {
    professionOptions,
    roleOptions,
    levelOftrainingOptions,
  };
};

/**
 * Options for the signup / profile-edit dropdowns, sourced from
 * `GET /profile-fields`
 */
const useProfileOptions = ({ profession, role }) => {
  const LANG_PROFILE = useSelector(getLang('PROFILE_OPTIONS'));

  const { data: profileFieldsByGroup } = useProfileFields();

  const fieldsByKey = useMemo(
    () => buildProfileFieldsByKey(profileFieldsByGroup),
    [profileFieldsByGroup],
  );

  const mapEnumOptions = (values, labels) =>
    (Array.isArray(values) ? values : Object.values(values || {}))
      .map(el => ({ value: el, label: labels?.[el] ?? el }))
      .sort((a, b) => sortByAlphabet(a?.label, b?.label));

  const departmentOptions = useMemo(
    () => mapProfileFieldOptions(fieldsByKey.department?.options),
    [fieldsByKey],
  );

  const { rotationOptions } = useRotationOptions();

  const professionOptions = useMemo(
    () => mapProfileFieldOptions(fieldsByKey.profession?.options),
    [fieldsByKey],
  );

  // Dependent: role filtered by the selected profession via `parentOptionId`.
  // null = field not configured / no profession selected; [] = selected
  // profession has no roles. Both leave the field disabled.
  const roleOptions = useMemo(() => {
    const field = fieldsByKey.role;
    if (!field?.options?.length || !profession) return null;
    const parent = fieldsByKey.profession?.options?.find(o => o.code === profession);
    if (!parent) return null;
    return mapProfileFieldOptions(field.options.filter(o => o.parentOptionId === parent._id));
  }, [fieldsByKey, profession]);

  // Dependent: levelOfTraining filtered by the selected role.
  const levelOftrainingOptions = useMemo(() => {
    const field = fieldsByKey.levelOfTraining;
    if (!field?.options?.length || !role) return null;
    const parent = fieldsByKey.role?.options?.find(o => o.code === role);
    if (!parent) return null;
    return mapProfileFieldOptions(field.options.filter(o => o.parentOptionId === parent._id));
  }, [fieldsByKey, role]);

  const specialtyOptions = useMemo(
    () => mapProfileFieldOptions(fieldsByKey.specialty?.options),
    [fieldsByKey],
  );

  const divisionOptions = useMemo(
    () => mapProfileFieldOptions(fieldsByKey.division?.options),
    [fieldsByKey],
  );

  const unitOptions = useMemo(
    () => mapProfileFieldOptions(fieldsByKey.unit?.options),
    [fieldsByKey],
  );

  const fieldLabels = useMemo(
    () => ({
      department: fieldsByKey.department?.displayLabel,
      profession: fieldsByKey.profession?.displayLabel,
      role: fieldsByKey.role?.displayLabel,
      levelOfTraining: fieldsByKey.levelOfTraining?.displayLabel,
      division: fieldsByKey.division?.displayLabel,
      specialty: fieldsByKey.specialty?.displayLabel,
      unit: fieldsByKey.unit?.displayLabel,
    }),
    [fieldsByKey],
  );

  const fieldHasOptions = useMemo(
    () => ({
      department: !!fieldsByKey.department?.options?.length,
      profession: !!fieldsByKey.profession?.options?.length,
      role: !!fieldsByKey.role?.options?.length,
      levelOfTraining: !!fieldsByKey.levelOfTraining?.options?.length,
      division: !!fieldsByKey.division?.options?.length,
      specialty: !!fieldsByKey.specialty?.options?.length,
      unit: !!fieldsByKey.unit?.options?.length,
    }),
    [fieldsByKey],
  );

  const timezoneOptions = mapEnumOptions(moment.tz.names());
  const languageOptions = useMemo(() => mapEnumOptions(LANGUAGES, LANG_PROFILE), [LANG_PROFILE]);

  return {
    departmentOptions,
    rotationOptions,
    professionOptions,
    roleOptions,
    levelOftrainingOptions,
    unitOptions,
    timezoneOptions,
    languageOptions,
    specialtyOptions,
    divisionOptions,
    fieldLabels,
    fieldHasOptions,
  };
};

/**
 * Sets of parent codes that have child options, so a dependent field can be
 * required when its selected parent actually has children. Derived from
 * `GET /profile-fields`; a child with no configured options
 * is never required. Used to build the profile-form validation schemas (and drive
 * the required indicators).
 */
const useProfileRequirementSets = () => {
  const { data: profileFieldsByGroup } = useProfileFields();

  return useMemo(() => {
    const all = flattenProfileFields(profileFieldsByGroup);
    const byKey = all.reduce((acc, field) => {
      acc[field.fieldKey] = field;
      return acc;
    }, {});

    const parentCodesWithChildren = (parentKey, childKey) => {
      const parentField = byKey[parentKey];
      const childField = byKey[childKey];
      if (!childField?.options?.length || !parentField) {
        return new Set();
      }
      const parentIdsWithChildren = new Set(
        childField.options.map(o => o.parentOptionId).filter(Boolean),
      );
      return new Set(
        parentField.options.filter(o => parentIdsWithChildren.has(o._id)).map(o => o.code),
      );
    };

    return {
      professionsWithRoles: parentCodesWithChildren('profession', 'role'),
      rolesWithLevels: parentCodesWithChildren('role', 'levelOfTraining'),
      optionCodesByField: all.reduce((acc, field) => {
        acc[field.fieldKey] = new Set((field.options || []).map(o => o.code));
        return acc;
      }, {}),
      // Field keys the org has actually configured (have options). Mirrors
      // `fieldHasOptions` in useProfileOptions — an unconfigured field is hidden,
      // so a required rule on it must be relaxed or the form can never validate.
      configuredFields: new Set(all.filter(f => f.options?.length).map(f => f.fieldKey)),
    };
  }, [profileFieldsByGroup]);
};

const useProfileLang = () => {
  const profileLang = useSelector(getLang('PROFILE_OPTIONS'));
  const tagsLang = useSelector(getLang('TAGS'));
  const divisionLang = useSelector(getLang('DIVISION'));
  const specialtyLang = useSelector(getLang('SPECIALTY'));
  const locations = useSelector(getLabelsList, shallowEqual);
  const workspaces = useSelector(getWorkspaces);
  const { data: profileFieldsByGroup } = useProfileFields();

  const optionLabelsByCode = useMemo(() => {
    const all = flattenProfileFields(profileFieldsByGroup);
    const map = {};
    for (const field of all) {
      for (const option of field.options || []) {
        map[option.code] = option.label || option.code;
      }
    }
    return map;
  }, [profileFieldsByGroup]);

  const userProfileOptions = useMemo(
    () => ({
      ...profileLang,
      ...tagsLang,
      ...divisionLang,
      ...specialtyLang,
      ...optionLabelsByCode,
    }),
    [profileLang, tagsLang, divisionLang, specialtyLang, optionLabelsByCode],
  );

  const locationsOptions = useMemo(() => {
    return (
      locations
        ?.sort((a, b) => sortByAlphabet(a.name, b.name))
        ?.map(({ code, name }) => ({ code, name })) ?? []
    );
  }, [locations]);

  const workspacesOptions = useMemo(() => {
    return (
      workspaces
        ?.sort((a, b) => sortByAlphabet(a.name, b.name))
        ?.map(({ id, name }) => ({ id, name })) ?? []
    );
  }, [workspaces]);

  const getWorkspaceName = useCallback(
    id => workspacesOptions.find(({ id: workspaceId }) => workspaceId === id)?.name,
    [workspacesOptions],
  );

  const getWorkspaceId = useCallback(
    name => workspacesOptions.find(({ name: workspaceName }) => workspaceName === name)?.id,
    [workspacesOptions],
  );

  const getValueFromLang = useCallback(
    value => userProfileOptions[value] ?? value ?? '-',
    [userProfileOptions],
  );

  const getMultipleValuesFromLang = useCallback(
    list => {
      if (_.isEmpty(list)) return '-';
      return (
        list
          ?.map(value => userProfileOptions[value])
          ?.sort((a, b) => sortByAlphabet(a, b))
          ?.join(', ') ?? '-'
      );
    },
    [userProfileOptions],
  );

  const getLocationsValues = useCallback(
    list => {
      if (_.isEmpty(list)) return '-';
      return (
        list
          ?.map(value => locationsOptions.find(({ code }) => code === value)?.name)
          ?.sort((a, b) => sortByAlphabet(a, b))
          ?.join(', ') ?? '-'
      );
    },
    [locationsOptions],
  );

  const getWorkspaceValues = useCallback(
    value => getWorkspaceName(value) ?? '-',
    [getWorkspaceName],
  );

  return {
    getValueFromLang,
    getMultipleValuesFromLang,
    getLocationsValues,
    getWorkspaceValues,
    getWorkspaceName,
    getWorkspaceId,
    userProfileOptions,
    locationsOptions,
    workspacesOptions,
  };
};

export {
  useProfileOptions,
  useProfileTagsOption,
  useProfileLang,
  useProfileOptionsField,
  useProfileRequirementSets,
};
